/**
 * CommentsHistoryView — render del historial completo de comments de
 * un artifact, agrupado por (version_number DESC, review_cycle DESC).
 *
 * Vivía como helper interno en `CommentsPanel` (editor); ahora es
 * compartido con `CommentsViewerPanel` para que la vista "Historial"
 * sea consistente en ambos paneles (mismo agrupado, mismas acciones,
 * mismo render del thread).
 *
 * Read-only: el historial es evidencia inmutable de auditoría. Las
 * acciones (status transitions, edit/delete) están deshabilitadas
 * deliberadamente desde el caller via `showStatusActions=false` y
 * `showEditDeleteActions=false`.
 */

import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import type { ArtifactComment } from '../../../api/comments'
import { CommentThreadCard, groupCommentsByThread } from './CommentThreadCard'
import type { TemplateI18nSection } from '../official-viewer/section_types'

export function CommentsHistoryView({
  comments,
  currentUserId,
  artifactAuthorId,
  displayNameByUserId,
  templateI18nMap,
}: {
  comments: ArtifactComment[]
  currentUserId: string | null
  artifactAuthorId: string | null
  displayNameByUserId: Map<string, string>
  templateI18nMap: Map<string, TemplateI18nSection>
}) {
  const { t } = useTranslation('projects')

  // Agrupar por (version_number, review_cycle). El backend devuelve
  // DESC; re-key-amos en un Map para detectar grupos contiguos.
  const groups = useMemo(() => {
    type GroupKey = string
    const map = new Map<
      GroupKey,
      {
        versionNumber: number | null
        reviewCycle: number
        comments: ArtifactComment[]
      }
    >()
    for (const c of comments) {
      const key = `${c.version_number ?? 'n'}::${c.review_cycle}`
      const existing = map.get(key)
      if (existing) {
        existing.comments.push(c)
      } else {
        map.set(key, {
          versionNumber: c.version_number,
          reviewCycle: c.review_cycle,
          comments: [c],
        })
      }
    }
    return Array.from(map.values())
  }, [comments])

  if (groups.length === 0) {
    return (
      <div className="px-5 py-12 text-center">
        <p className="font-display text-sm font-semibold text-ink">
          {t('comments_panel.history_empty.title')}
        </p>
        <p className="mt-1 text-xs text-ink-muted">
          {t('comments_panel.history_empty.body')}
        </p>
      </div>
    )
  }

  return (
    <div className="flex-1 min-h-0 overflow-y-auto">
      {groups.map((group) => {
        const threads = groupCommentsByThread(group.comments)
        if (threads.length === 0) return null
        const headerKey = `${group.versionNumber ?? 'n'}::${group.reviewCycle}`
        return (
          <section key={headerKey} className="border-b border-line">
            <header className="sticky top-0 bg-surface-muted/80 backdrop-blur-sm border-b border-line px-4 py-1.5 text-[10px] font-medium uppercase tracking-wider text-ink-subtle z-10">
              {group.versionNumber !== null
                ? t('comments_panel.history_group.version_cycle', {
                    v: group.versionNumber,
                    c: group.reviewCycle,
                  })
                : t('comments_panel.history_group.unknown_version_cycle', {
                    c: group.reviewCycle,
                  })}
            </header>
            <ul className="divide-y divide-line">
              {threads.map((thread) => (
                <li key={thread.root.id}>
                  <CommentThreadCard
                    thread={thread}
                    currentUserId={currentUserId}
                    artifactAuthorId={artifactAuthorId}
                    displayNameByUserId={displayNameByUserId}
                    sectionTitle={sectionTitle(
                      thread.root.section_id,
                      thread.root.instance_key,
                      templateI18nMap,
                    )}
                    showStatusActions={false}
                    showEditDeleteActions={false}
                  />
                </li>
              ))}
            </ul>
          </section>
        )
      })}
    </div>
  )
}

function sectionTitle(
  sectionId: string,
  instanceKey: string,
  templateI18nMap: Map<string, TemplateI18nSection>,
): string {
  const i18n = templateI18nMap.get(sectionId)
  const base = i18n?.title ?? sectionId
  if (instanceKey && instanceKey !== sectionId) {
    return `${base} · ${instanceKey}`
  }
  return base
}
